import { useCallback } from "react";

import { createStorage } from "..";

interface IAlertsStorage {
  getAlert: () => unknown;
  setAlert: (
    alertName: string,
    id: string,
    values: unknown,
  ) => Promise<boolean>;
}

// Type guard to ensure data is a valid object for spreading
const isValidObject = (data: unknown): data is Record<string, unknown> => {
  return data !== null && typeof data === "object";
};

// Type guard to ensure alert is the expected structure
const isAlertValid = (
  data: unknown,
  id: string,
): data is Record<string, Record<string, string>> => {
  return isValidObject(data) && typeof data[id] === "object";
};

const alertsStorage = createStorage({ type: "local" });

const useAlertsStorage = (): IAlertsStorage => {
  const setAlert = useCallback(
    async (
      alertName: string,
      id: string,
      values: unknown,
    ): Promise<boolean> => {
      const alerts = alertsStorage.getItem("ui-alerts");
      const idAlerts = isAlertValid(alerts, id) ? alerts[id] : {};

      return alertsStorage.setItem("ui-alerts", {
        ...(isValidObject(alerts) ? alerts : {}),
        [id]: { ...idAlerts, [alertName]: values },
      });
    },
    [],
  );

  const getAlert = useCallback((): unknown => {
    return alertsStorage.getItem("ui-alerts");
  }, []);

  return { getAlert, setAlert };
};

export { isValidObject, useAlertsStorage };
